import {ErrorBoundary} from '@github-ui/react-core/error-boundary'
import {InfoIcon} from '@primer/octicons-react'
import {IconButton} from '@primer/react'
import {useRef, useState} from 'react'
import {OverviewSidePanel} from './OverviewSidePanel'
import {useDescriptionLiveUpdates} from '../../hooks/use-description-live-updates'
import {useDescriptionData} from '../../page-data/loaders/use-description-data'
import type {SafeHTMLString} from '@github-ui/safe-html'

/**
 * Renders a button that opens the overview side panel
 */
export function OpenOverviewSidePanelButton({aliveChannel}: {aliveChannel: string}) {
  const [overviewSidePanelIsOpen, setOverviewSidePanelIsOpen] = useState(false)
  const toggleSidePanelRef = useRef<HTMLButtonElement>(null)

  // eslint-disable-next-line github/unescaped-html-literal -- Known safe static fallback content
  const emptyDescription = `<p class="text-italic">No description provided.</p>` as SafeHTMLString

  const {data: description, isLoading} = useDescriptionData()
  useDescriptionLiveUpdates(aliveChannel)
  const bodyHtml = (description?.bodyHtml || emptyDescription) as SafeHTMLString

  return (
    <div className="d-flex flex-items-center">
      <ErrorBoundary
        fallback={
          <IconButton
            inactive
            aria-label="The overview side panel cannot currently be opened."
            icon={InfoIcon}
            size="small"
            variant="invisible"
          />
        }
      >
        <IconButton
          ref={toggleSidePanelRef}
          aria-label="Open overview side panel"
          icon={InfoIcon}
          size="small"
          onClick={() => {
            setOverviewSidePanelIsOpen(true)
          }}
        />
        {overviewSidePanelIsOpen && (
          <OverviewSidePanel
            bodyHtml={bodyHtml}
            isLoading={isLoading}
            onClose={() => setOverviewSidePanelIsOpen(false)}
          />
        )}
      </ErrorBoundary>
    </div>
  )
}

try{ OpenOverviewSidePanelButton.displayName ||= 'OpenOverviewSidePanelButton' } catch {}